/**
 * Shared sidebar navigation config — the single source of truth for the nav
 * sections AND each section's icon.
 *
 * The sidebar (Sidebar.tsx) renders these sections; the breadcrumb
 * (BreadcrumbHost) looks up the leading icon for the active section via
 * `getNavIcon`, so every page's breadcrumb shows "icon + label" matching the
 * sidebar (e.g. the Reports icon before "Reports"). Keeping both off one
 * config means adding a nav item updates the sidebar and the breadcrumb icon
 * together.
 */

import {
  ChartPieSlice,
  Users,
  Files,
  Target,
  BellRinging,
  Kanban,
  ListMagnifyingGlass,
  GearSix,
  Headset,
  Megaphone,
  type Icon,
} from "@phosphor-icons/react";

export interface NavItem {
  label: string;
  to: string;
  icon: Icon;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    label: "GENERAL",
    items: [
      { label: "Clients", to: "/clients", icon: Users },
      { label: "Reports", to: "/reports", icon: Files },
      { label: "Dashboard", to: "/dashboard", icon: ChartPieSlice },
    ],
  },
  {
    label: "TOOLS",
    items: [
      { label: "Goals", to: "/goals", icon: Target },
      { label: "Alerts", to: "/alerts", icon: BellRinging },
      { label: "Tasks", to: "/tasks", icon: Kanban },
      { label: "Activity", to: "/activity", icon: ListMagnifyingGlass },
    ],
  },
  {
    label: "SUPPORT",
    items: [
      { label: "Settings", to: "/settings", icon: GearSix },
      { label: "Support", to: "/support", icon: Headset },
      { label: "What's new", to: "/whats-new", icon: Megaphone },
    ],
  },
];

// Flat lookups for breadcrumb leading-icon resolution, built once from the
// config above.
const iconByPath = new Map<string, Icon>();
const iconByLabel = new Map<string, Icon>();
for (const section of navSections) {
  for (const item of section.items) {
    iconByPath.set(item.to, item.icon);
    iconByLabel.set(item.label, item.icon);
  }
}

/**
 * Resolve the sidebar icon for a breadcrumb item. Matches by exact path, then
 * by path prefix (e.g. "/reports/123" → Reports), then by exact label (the
 * leading crumb of simple pages carries only a label). Returns null when the
 * crumb is not a top-level nav section, so no icon is rendered for it.
 */
export function getNavIcon(item: { label: string; to?: string }): Icon | null {
  if (item.to) {
    const exact = iconByPath.get(item.to);
    if (exact) return exact;
    for (const [path, icon] of iconByPath) {
      if (item.to === path || item.to.startsWith(path + "/")) return icon;
    }
  }
  return iconByLabel.get(item.label) ?? null;
}
